import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ViewToggle } from "@/components/ui/view-toggle";
import type { ViewPreference } from "@/lib/types";
import { MoreVertical, Pencil, PlusCircle, Settings, Trash2, Users } from "lucide-react";
import { Link } from "react-router-dom";

interface MobileActionsMenuProps {
  viewPreference: ViewPreference;
  onViewChange: (view: ViewPreference) => void;
  onManageCategories: () => void;
  onOpenMembers: () => void;
  canManage: boolean;
  isOwner: boolean;
  canAdd: boolean;
  recipeBoxId: string;
  onEditBox?: () => void;
  onDeleteBox?: () => void;
}

/**
 * Mobile-optimized actions menu that consolidates all recipe box actions
 * into a single menu button to save space on mobile devices
 */
export function MobileActionsMenu({
  viewPreference,
  onViewChange,
  onManageCategories,
  onOpenMembers,
  canManage,
  isOwner,
  canAdd,
  recipeBoxId,
  onEditBox,
  onDeleteBox,
}: MobileActionsMenuProps) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="sm:hidden">
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-auto max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Recipe Options</SheetTitle>
          <SheetDescription>Manage your recipes and settings</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          {/* View Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">View Style</p>
              <p className="text-xs text-muted-foreground">Choose how recipes are displayed</p>
            </div>
            <ViewToggle view={viewPreference} onViewChange={onViewChange} />
          </div>

          <div className="border-t pt-4 space-y-2">
            {/* Add Recipe */}
            {canAdd && (
              <Button asChild className="w-full justify-start" size="sm">
                <Link to={`/add-recipe?recipeBoxId=${recipeBoxId}`}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Recipe
                </Link>
              </Button>
            )}

            {/* Members */}
            <Button
              variant="outline"
              className="w-full justify-start"
              size="sm"
              onClick={onOpenMembers}
            >
              <Users className="mr-2 h-4 w-4" />
              Members
            </Button>

            {/* Category Management */}
            {canManage && (
              <Button
                variant="outline"
                className="w-full justify-start"
                size="sm"
                onClick={onManageCategories}
              >
                <Settings className="mr-2 h-4 w-4" />
                Manage Categories
              </Button>
            )}

            {/* Owner-only box actions */}
            {isOwner && (
              <>
                {onEditBox && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    size="sm"
                    onClick={onEditBox}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Recipe Box
                  </Button>
                )}
                {onDeleteBox && (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-destructive hover:text-destructive"
                    size="sm"
                    onClick={onDeleteBox}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Recipe Box
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

